import { useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { useUserEvents, type NudgeEvent } from '../hooks/useUserEvents';
import styles from './NudgeToast.module.css';

const AUTO_DISMISS_MS = 6000;

/**
 * App-global realtime nudge toast — the PWA analogue of the iOS NudgeCenter
 * banner. Renders the latest `nudge` received on the authenticated user's room,
 * routes to the thread on tap, and auto-dismisses. Suppressed when the user is
 * already viewing the nudge's thread (same focus-guard as NudgeCenter), so we
 * don't interrupt someone already in the conversation.
 */
export function NudgeToast() {
  const nudge = useUserEvents();
  const [visible, setVisible] = useState<NudgeEvent | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!nudge) return;
    // Focus guard: don't toast a thread the user is already looking at. Bail
    // WITHOUT touching the current toast or its timer — a suppressed nudge
    // must not strand an earlier toast on screen with its auto-dismiss dead
    // (which is why this effect has no per-run cleanup: it would clear the
    // visible toast's timer before this guard runs).
    if (nudge.threadId && window.location.pathname === `/thread/${nudge.threadId}`) {
      return;
    }
    setVisible(nudge);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setVisible(null), AUTO_DISMISS_MS);
  }, [nudge]);

  // Unmount-only: drop any pending auto-dismiss timer.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!visible) return null;

  const openThread = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (visible.threadId) route(`/thread/${visible.threadId}`);
    setVisible(null);
  };

  const dismiss = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(null);
  };

  return (
    <div class={styles.toast} role="status" aria-live="polite">
      <button
        type="button"
        class={styles.body}
        onClick={openThread}
        disabled={!visible.threadId}
      >
        <span class={styles.title}>{visible.title}</span>
        {visible.message && <span class={styles.message}>{visible.message}</span>}
      </button>
      <button
        type="button"
        class={styles.dismiss}
        onClick={dismiss}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
